import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  SimpleChanges,
} from '@angular/core';
import {
  StorageFile,
  StorageFilesDetails,
  StorageSelectorFilter,
  StorageSelectorType,
  StorageUtilsService,
} from '../../services/storage-utils.service';
import { StorageService } from '../../services/storage.service';
import { ListItem, ListOperation } from 'src/app/components/scrolling-list';
import { StorageApiSearch } from 'src/app/api/services/api-utils.service';

@Component({
  selector: 'storage-grid-view',
  templateUrl: './storage-grid-view.component.html',
  styleUrls: ['./storage-grid-view.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StorageGridViewComponent implements OnChanges, OnDestroy {
  @ViewChild('gridContainer', { static: false }) gridContainer: ElementRef;

  @Input() files: ListItem<StorageFile>[];
  @Input() folderPath: string = '';
  @Input() filesDetails: StorageFilesDetails;
  @Input() fileListVisible: boolean;
  @Input() search: StorageApiSearch;
  @Input() isEmpty: boolean;
  @Input() listOperations: ListOperation<StorageFile>[] = [];
  @Input({ required: true }) selectorType: StorageSelectorType;
  @Input({ required: true }) selectorFilter: StorageSelectorFilter;
  @Input({ required: true }) storageFull: boolean;
  @Output() fileClick: EventEmitter<ListItem<StorageFile>> = new EventEmitter<
    ListItem<StorageFile>
  >();
  @Output() fileSelect: EventEmitter<ListItem<StorageFile>> = new EventEmitter<
    ListItem<StorageFile>
  >();
  @Output() uploadFiles: EventEmitter<void> = new EventEmitter<void>();
  @Output() operationClick: EventEmitter<{
    file: ListItem<StorageFile>;
    operationName: string;
  }> = new EventEmitter<{
    file: ListItem<StorageFile>;
    operationName: string;
  }>();

  visibleFiles: ListItem<StorageFile>[] = [];
  private allFiles: ListItem<StorageFile>[] = [];
  private maxLoadedIndex = 0;
  private isLoadingFiles = false;
  private isGridSetup = false;
  private isSettingUpGrid = false;
  private lastSearchQuery: string = '';
  private scrollHandler: (() => void) | null = null;
  private setupTimeout: any = null;

  protected get isTrashFolder(): boolean {
    return this.storageUtilsService.isTrashFolder(this.folderPath);
  }

  constructor(
    public storageUtilsService: StorageUtilsService,
    public storageService: StorageService,
    private cdr: ChangeDetectorRef,
  ) {}

  private get gridElement(): HTMLElement | null {
    return this.gridContainer?.nativeElement || null;
  }

  isFirstFile(file: ListItem<StorageFile>): boolean {
    return (file.item as StorageFile).isFirstFile || false;
  }

  isThrottled(file: ListItem<StorageFile>): boolean {
    return (file.item as StorageFile).isThrottled || false;
  }

  @HostListener('window:resize')
  onResize() {
    this.updateVisibleFiles();
    setTimeout(() => this.ensureViewportFilled());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.files) {
      this.cleanupGrid();
      if (this.files?.length) {
        this.processFiles();
        this.scheduleGridSetup();
      } else {
        this.allFiles = [];
        this.visibleFiles = [];
        this.maxLoadedIndex = 0;
        this.cdr.detectChanges();
      }
    }
  }

  ngOnDestroy() {
    this.cleanupGrid();
  }

  private scheduleGridSetup() {
    if (this.setupTimeout) {
      clearTimeout(this.setupTimeout);
    }
    this.setupTimeout = setTimeout(() => {
      this.setupGrid();
      this.setupTimeout = null;
    });
  }

  private setupGrid() {
    if (this.gridElement && !this.isGridSetup && !this.isSettingUpGrid) {
      this.isSettingUpGrid = true;
      this.isGridSetup = true;
      this.scrollHandler = () => this.onElementScroll();
      this.gridElement.addEventListener('scroll', this.scrollHandler);
      this.updateVisibleFiles();

      setTimeout(() => {
        this.isSettingUpGrid = false;
        this.ensureViewportFilled();
      });
    }
  }

  private cleanupGrid() {
    if (this.gridElement && this.scrollHandler) {
      this.gridElement.removeEventListener('scroll', this.scrollHandler);
    }
    this.scrollHandler = null;
    this.isGridSetup = false;
    this.isSettingUpGrid = false;
    if (this.setupTimeout) {
      clearTimeout(this.setupTimeout);
      this.setupTimeout = null;
    }
  }

  private ensureViewportFilled(): void {
    if (!this.gridElement) {
      return;
    }

    while (
      this.gridElement.scrollHeight <= this.gridElement.offsetHeight &&
      this.maxLoadedIndex < this.allFiles.length
    ) {
      const oldMaxLoaded = this.maxLoadedIndex;
      this.loadMoreItems();

      if (this.maxLoadedIndex === oldMaxLoaded || !this.gridElement) {
        break;
      }
    }
  }

  private onElementScroll(): void {
    if (
      this.maxLoadedIndex >= this.allFiles.length ||
      this.isLoadingFiles ||
      !this.gridElement ||
      this.isSettingUpGrid
    ) {
      return;
    }

    const offsetHeight = this.gridElement.offsetHeight;
    const scrollTop = this.gridElement.scrollTop;
    const scrollHeight = this.gridElement.scrollHeight;
    const isNearBottom = offsetHeight + scrollTop >= scrollHeight - 210;

    if (isNearBottom) {
      this.loadMoreItems();
    }
  }

  private loadMoreItems(): void {
    if (
      this.maxLoadedIndex >= this.allFiles.length ||
      this.isLoadingFiles ||
      !this.gridElement ||
      this.isSettingUpGrid
    ) {
      return;
    }

    this.isLoadingFiles = true;

    const oldMaxLoaded = this.maxLoadedIndex;
    this.maxLoadedIndex = Math.min(
      this.maxLoadedIndex + 15,
      this.allFiles.length,
    );

    const newItems = this.allFiles.slice(oldMaxLoaded, this.maxLoadedIndex);
    this.visibleFiles.push(...newItems);

    this.cdr.detectChanges();
    this.isLoadingFiles = false;
  }

  private processFiles(resetMaxLoaded = false) {
    const folders = this.storageUtilsService.filterFolders(
      this.files,
      this.search,
      this.storageFull,
    );
    const files = this.storageUtilsService.filterFiles(
      this.files,
      this.search,
      this.storageFull,
      true,
    );

    if (folders.length > 0 && files.length > 0) {
      (files[0].item as StorageFile).isFirstFile = true;
    }

    this.allFiles = [...folders, ...files];

    if (resetMaxLoaded) {
      this.maxLoadedIndex = 0;
    } else {
      this.maxLoadedIndex = Math.min(40, this.allFiles.length);
    }

    this.updateVisibleFiles();
  }

  checkSearchChange() {
    const currentQuery = this.search?.query || '';
    if (currentQuery !== this.lastSearchQuery) {
      this.lastSearchQuery = currentQuery;
      this.cleanupGrid();
      this.visibleFiles = [];
      this.processFiles(true); // Reset maxLoadedIndex
      this.scheduleGridSetup();
      this.cdr.detectChanges();
    }
  }

  private updateVisibleFiles() {
    if (!this.allFiles.length) {
      this.visibleFiles = [];
      this.cdr.detectChanges();
      return;
    }

    this.visibleFiles = this.allFiles.slice(0, this.maxLoadedIndex);
    this.cdr.detectChanges();
  }

  trackByFileName(index: number, file: ListItem<StorageFile>): string {
    return file?.item.name || `file-${index}`;
  }
}
